import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, DollarSign, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";

/**
 * Houston Apartment Match Quiz — landing page with a direct external link
 * to the live Typeform. Previously used Typeform popup-embed (form ID
 * 01KSR618J22928PPVGXE2VHDG8) which was redirecting visitors to typeform.com
 * homepage; switched to the public form URL for reliability.
 */
const MatchQuizPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2342] via-[#0d2d54] to-[#102a4f] py-10 px-4 md:py-14">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="text-center text-white mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] leading-tight"
            data-testid="match-quiz-title"
          >
            Find Your Perfect Houston<br className="hidden sm:block" /> Apartment in 60 Seconds
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-sm sm:text-base text-white/70 mt-3 max-w-xl mx-auto"
          >
            Answer a few quick questions and get a personalized list with hidden specials, pricing, and availability — all free.
          </motion.p>
        </div>

        {/* Direct link to live Typeform — opens in a new tab */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12 text-center"
          data-testid="match-quiz-card"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']">
            Ready to find your next place?
          </h2>
          <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-md mx-auto">
            Tap the button below to start the quiz. It takes about 60 seconds.
          </p>
          <a
            href={TYPEFORM_QUIZ_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="match-quiz-start-btn"
            className="inline-flex items-center gap-2 mt-7 px-8 py-4 bg-[#DE6E4B] text-white text-base sm:text-lg font-bold rounded-xl hover:bg-[#c85e3d] active:scale-[0.98] transition-all shadow-lg"
          >
            Start My Match <ExternalLink className="w-5 h-5" />
          </a>
          <p className="text-xs text-slate-400 mt-4">
            Opens the secure quiz in a new tab — fully mobile-friendly.
          </p>
        </motion.div>

        {/* Trust strip */}
        <div className="mt-6 grid grid-cols-3 gap-2 text-center">
          <TrustBadge icon={ShieldCheck} text="100% Free Service" />
          <TrustBadge icon={DollarSign}  text="You pay nothing" />
          <TrustBadge icon={Sparkles}    text="Unlisted specials" />
        </div>

        {/* Fallback CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-3 text-sm">
          <div className="text-white/60 inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Avg response: under 30 min
          </div>
          <a
            href="sms:9362241484"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#DE6E4B] text-white rounded-lg font-semibold hover:bg-[#c85e3d]"
            data-testid="match-quiz-text-fallback"
          >
            <MessageCircle className="w-4 h-4" /> Or text us: (936) 224-1484
          </a>
          <Link
            to="/find-apartment"
            className="text-white/60 hover:text-white underline text-xs"
            data-testid="match-quiz-react-alt"
          >
            Prefer a step-by-step form?
          </Link>
        </div>
      </div>
    </div>
  );
};

const TrustBadge = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center justify-center gap-1.5 text-[11px] sm:text-xs text-white/70 px-2 py-1.5 rounded-lg bg-white/5 backdrop-blur-sm">
    <Icon className="w-3.5 h-3.5" />
    <span>{text}</span>
  </div>
);

export default MatchQuizPage;
